const FALLBACK_SITE_URL = 'https://www.pixelmojo.io'

/**
 * The founder's live booking link.
 *
 * It is already handed to leads by the chat action card
 * (components/chat/ActionCard.tsx), four lead-confirmation emails
 * (lib/lead-confirmation-emails.ts), the recap generator, and the sales system
 * prompt — each with its own hardcoded copy of this string. Those copies
 * should migrate here when they are next touched; this constant exists so no
 * NEW call site adds a seventh.
 *
 * Deliberately NOT used as the destination of a "Book a call" CTA: those go to
 * /contact-us so the brief is captured as a lead row first. The booking link
 * is offered AFTER submission, which is the same order the confirmation email
 * uses.
 */
export const BOOKING_URL = 'https://calendly.com/lloyd-pixelmojo/30min'
export const CANONICAL_PRODUCTION_HOST = 'www.pixelmojo.io'
// Only the bare apex and the canonical www host are treated as the production
// origin. Everything else (localhost, 127.0.0.1, Vercel previews, staging, and
// production subdomains such as csp-report.pixelmojo.io) is preserved as-is.
export const PRODUCTION_HOSTS = new Set([
  'pixelmojo.io',
  CANONICAL_PRODUCTION_HOST,
])
const rawSiteUrl = process.env.NEXT_PUBLIC_SITE_URL

const normalizedSiteUrl = (() => {
  if (!rawSiteUrl) {
    return FALLBACK_SITE_URL
  }

  try {
    const parsedUrl = new URL(rawSiteUrl)

    const isLocalHost = ['localhost', '127.0.0.1'].includes(parsedUrl.hostname)

    if (isLocalHost && process.env.NODE_ENV === 'production') {
      return FALLBACK_SITE_URL
    }

    return parsedUrl.origin
  } catch {
    return FALLBACK_SITE_URL
  }
})()

export function buildCanonicalUrl(path: string) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return new URL(normalizedPath, normalizedSiteUrl).toString()
}

/**
 * Normalize a configured base URL into a safe origin for building public links
 * (e.g. shareable report URLs).
 *
 * Behavior:
 *  - Missing or malformed value -> the canonical production origin.
 *  - The bare production host `pixelmojo.io` is rewritten to `www.pixelmojo.io`,
 *    forced to HTTPS, with any port removed (the canonical production origin
 *    never carries a port).
 *  - The canonical `www.pixelmojo.io` host is returned as the canonical origin.
 *  - Every other host — localhost, 127.0.0.1, Vercel preview domains, staging,
 *    and production subdomains such as csp-report.pixelmojo.io — is preserved.
 *  - Only the origin is returned; path, query, hash, and credentials are dropped.
 *
 * Pure (aside from a warning on malformed input) so it can be unit-tested with
 * no environment or network access.
 */
export function normalizeProductionOrigin(rawUrl?: string): string {
  if (!rawUrl) {
    return FALLBACK_SITE_URL
  }

  let parsedUrl: URL
  try {
    parsedUrl = new URL(rawUrl.trim())
  } catch {
    // Never emit an invalid link; fall back to the canonical origin. Do NOT log
    // the raw value — it may contain credentials or other sensitive data.
    console.warn(
      '[site-config] Ignoring malformed base URL; using canonical origin.'
    )
    return FALLBACK_SITE_URL
  }

  if (PRODUCTION_HOSTS.has(parsedUrl.hostname)) {
    // Canonicalize the production host: force www + HTTPS, drop any port/path.
    return `https://${CANONICAL_PRODUCTION_HOST}`
  }

  // Non-production hosts stay exactly as configured. `origin` already strips any
  // path, query, hash, and credentials.
  return parsedUrl.origin
}

export const SITE_URL = normalizedSiteUrl
